// Die EINE App-Kopfzeile (Referenz: docs/design/01-roadmap-expanded.png).
//
// WOFUER: bis hierher hatte `Layout` ueberhaupt keine Kopfzeile — jede Route
// baute ihren eigenen Seitenkopf (Audit: 8 Bauarten ueber 15 Routen, 4
// H1-Varianten). Diese Leiste ersetzt sie alle: links der Seitentitel aus
// `pageHeaderSig` (via `ui/PageHeader`), rechts der GLOBALE Bereich.
//
// Der globale Bereich (Suche mit ⌘K-Hinweis, Glocke, Nutzer-Avatar mit Menue)
// sass bisher IN der Seitenleiste; er zieht mit diesem Paket hierher. Auf
// schmalen Fenstern ersetzt die Leiste zusaetzlich die zwei frei ueber dem
// Inhalt schwebenden Knoepfe (Menue links, Suche rechts), die das Audit als
// „unverankert" bemaengelt hat — der Menue-Ausloeser sitzt jetzt hier links.

import { useEffect, useRef, useState } from "preact/hooks";
import { LogOut, Menu, Search } from "lucide-preact";
import { pageHeaderSig, sidebarOpenSig } from "../lib/pageHeader";
import { PageHeader } from "./ui/PageHeader";
import { NotificationBell } from "./NotificationBell";
import { UserAvatar } from "./UserAvatar";
import { openPalette } from "./CommandPalette";
import { useMe, logout } from "../lib/auth";

/** Nutzer-Avatar mit Klappmenue (Name, E-Mail, Abmelden). */
function UserMenu(): preact.JSX.Element | null {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Schliessen bei Klick ausserhalb und bei Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const user = me.user;
  if (!user) return null;

  return (
    <div ref={wrapRef} class="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Nutzermenü öffnen"
        title={`${user.name} · ${user.email}`}
        class="flex items-center rounded-full p-0.5 transition-colors hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
      >
        <UserAvatar
          userId={user.id}
          size={28}
          name={user.name}
          avatarUrl={user.avatar_url}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Nutzermenü"
          class="absolute right-0 top-full z-40 mt-1 w-56 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-1 shadow-lg"
        >
          <div class="border-b border-[color:var(--color-outline-variant)] px-3 pb-2 pt-1">
            <p class="truncate text-sm font-medium text-[color:var(--color-on-surface)]">
              {user.name}
            </p>
            <p class="truncate text-micro text-[color:var(--color-on-surface-variant)]">
              {user.email}
            </p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-on-surface)] transition-colors hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)]"
          >
            <LogOut size={16} aria-hidden="true" />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

/** Suche, Glocke, Nutzermenü — unabhaengig davon, was die Route gesetzt hat. */
function GlobalActions(): preact.JSX.Element {
  return (
    <div class="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => openPalette()}
        aria-label="Globale Suche öffnen"
        title="Globale Suche — Cmd/Ctrl+K"
        class="inline-flex min-h-[36px] items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 text-xs text-[color:var(--color-on-surface-variant)] transition-colors hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 sm:min-h-[32px]"
      >
        <Search size={16} aria-hidden="true" />
        <span class="hidden sm:inline">Suche</span>
        <span class="hidden text-micro tracking-wide opacity-60 sm:inline">
          ⌘K
        </span>
      </button>
      <NotificationBell />
      <UserMenu />
    </div>
  );
}

export function AppHeader(): preact.JSX.Element {
  const config = pageHeaderSig.value;

  return (
    <header class="sticky top-0 z-30 border-b border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
      {/* EINE umbrechende Reihe, gesteuert ueber `order` und `basis` — bewusst
          NICHT zwei Reihen mit je eigenem `GlobalActions`.
          Der Fehler davor: Menue, Titel und globale Aktionen standen bei 390 px
          in einer starren Reihe; dem Titel blieben rund 60 px, die Ueberschrift
          stand als „Da…" da und der Untertitel lief ueber vier Zeilen.
          Der naheliegende Fix (zwei Reihen, je nach Breite eine ausgeblendet)
          haette `GlobalActions` ZWEIMAL ins Dokument gestellt. Fuer Nutzer und
          Hilfstechnik ist das unsichtbar (`hidden` = `display:none`), aber es
          verdoppelt Suche, Glocke und Avatar im Baum — Abfragen nach genau
          einem Knopf finden dann zwei. Mit Reihenfolge statt Duplikat existiert
          jedes Bedienelement genau einmal:
          schmal  -> Zeile 1: Menue + globale Aktionen, Zeile 2: Titelblock
          ab `md` -> eine Zeile: Titelblock + globale Aktionen (Menue entfaellt,
                     die Seitenleiste steht dort ohnehin). */}
      <div
        class={`flex flex-wrap gap-x-3 gap-y-1 px-4 sm:px-6 lg:px-8 ${
          config ? "items-start" : "min-h-[52px] items-center"
        }`}
      >
        <button
          type="button"
          onClick={() => {
            sidebarOpenSig.value = true;
          }}
          aria-label="Menü öffnen"
          class="order-1 inline-flex shrink-0 items-center justify-center rounded p-2 text-[color:var(--color-on-surface-variant)] transition-colors hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] md:hidden"
        >
          <Menu size={20} aria-hidden="true" />
        </button>

        {config ? (
          // Der Titelbereich stellt die Hoehe der Leiste. `PageHeader` klebt und
          // rahmt hier NICHT selbst — das macht dieses `<header>` aussen, sonst
          // gaebe es zwei Haarlinien und zwei Klebe-Ebenen uebereinander.
          <div class="order-3 min-w-0 basis-full pt-2 md:order-2 md:basis-0 md:flex-1">
            <PageHeader
              title={
                config.titleNode ? (
                  // EINE h1, und ihr zugaenglicher Name ist der Klartext aus
                  // `title`. Geloest ueber ein `aria-label` auf dem einzigen
                  // Kind der Ueberschrift: bei der Namensbildung aus dem Inhalt
                  // gewinnt das `aria-label` eines Nachfahren ueber dessen
                  // eigenen Inhalt, die h1 heisst also exakt `title`.
                  //
                  // Bewusst NICHT: ein verborgener Klartext NEBEN dem Knoten —
                  // ein Eingabefeld steuert seinen Wert zur Namensbildung bei,
                  // der Name wuerde sich verdoppeln ("Neubau Neubau").
                  // Bewusst NICHT: `aria-hidden` auf dem Knoten — ein inline
                  // editierbares Feld waere fuer Hilfstechnik dann unerreichbar.
                  // Der Knoten selbst bleibt bedienbar und traegt seine eigene
                  // Beschriftung (z.B. `aria-label="Projektname"`).
                  <span aria-label={config.title}>{config.titleNode}</span>
                ) : (
                  config.title
                )
              }
              subtitle={config.subtitle}
              breadcrumb={config.breadcrumb}
              actions={config.actions}
              tabs={config.tabs}
              sticky={false}
              class="mb-0 border-b-0"
            />
          </div>
        ) : (
          // Keine Route-Konfiguration: nur die Leiste, KEIN leeres h1 und kein
          // verschenkter Platz — eine schlanke Zeile statt eines Titelblocks.
          <div class="order-3 min-w-0 basis-full md:order-2 md:basis-0 md:flex-1" />
        )}

        {/* Schmal (order-2) rechts neben dem Menue in Zeile 1, breit (order-3)
            am rechten Rand der einen Reihe. `ml-auto` schiebt es schmal nach
            rechts, weil der Titelblock dort in der naechsten Zeile steht. */}
        <div
          class={`order-2 ml-auto flex shrink-0 items-center md:order-3 md:ml-0 ${
            config ? "md:pt-3" : ""
          }`}
        >
          <GlobalActions />
        </div>
      </div>
    </header>
  );
}
